"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import AuthModal from "./AuthModal";
import DefaultAvatar from "./DefaultAvatar";
import { useTranslation } from "react-i18next";
import { useProfile } from "@/lib/hooks/useProfile";
import ThemeToggle from "./ThemeToggle";
import { useAuthContext } from "@/lib/context/AuthContext";

export default function Navbar({ className = "" }: { className?: string }) {
  const { user, profile, loading } = useProfile();
  const { signOut } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const router = useRouter();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setShowMobileMenu(false);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // derive avatar URL from profile or auth metadata
  const avatarUrl = profile?.avatar_url ?? user?.user_metadata?.avatar_url;

  // Show nothing while loading to prevent hydration mismatch
  if (loading) return null;

  return (
    <nav className={`fixed top-0 left-0 right-0 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <div className="flex items-center gap-2">
            <Link href="/" className="flex items-center">
              <Image
                src="/white_logo.png"
                alt="Smarcation Logo"
                width={220}
                height={80}
                className="h-10 sm:h-14 w-auto"
                priority
              />
            </Link>
            <ThemeToggle className="ml-2" />
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {showMobileMenu ? (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden sm:flex items-center gap-4">
            {user ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={toggleProfileMenu}
                  className="flex items-center gap-2 focus:outline-none"
                >
                  {avatarUrl ? (
                    <div className="relative w-8 h-8 rounded-full ring-2 ring-white/30">
                      {avatarUrl.startsWith("blob:") ? (
                        <img
                          src={avatarUrl}
                          alt="Profile"
                          className="rounded-full w-8 h-8 object-cover"
                        />
                      ) : (
                        <Image
                          src={avatarUrl}
                          alt="Profile"
                          width={32}
                          height={32}
                          className="rounded-full"
                        />
                      )}
                    </div>
                  ) : (
                    <DefaultAvatar
                      name={user?.user_metadata?.full_name ?? user?.email ?? ""}
                      size="sm"
                    />
                  )}
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white/90 backdrop-blur-sm ring-1 ring-black/5">
                    <div className="py-1">
                      <Link
                        href="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/50"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        {t("auth.profile")}
                      </Link>
                      <Link
                        href="/trips"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/50"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        {t("auth.trips")}
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="cursor-pointer block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100/50"
                      >
                        {t("auth.signOut")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors"
              >
                {t("auth.login")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {showMobileMenu && (
        <div ref={mobileMenuRef} className="sm:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-slate-900/95 backdrop-blur-sm">
            {user ? (
              <>
                <Link
                  href="/profile"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-white/10"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t("auth.profile")}
                </Link>
                <Link
                  href="/trips"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-white/10"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t("auth.trips")}
                </Link>
                <button
                  onClick={handleSignOut}
                  className="cursor-pointer block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-white/10"
                >
                  {t("auth.signOut")}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  // Add a small delay to ensure the mobile menu is closed before the modal opens
                  setTimeout(() => setShowAuthModal(true), 100);
                }}
                className="cursor-pointer block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-white/10"
              >
                {t("auth.login")}
              </button>
            )}
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </nav>
  );
}
